// DashbordStock.jsx
import React, { useCallback, useEffect, useState } from 'react'
import './dashbordStock.scss';
import JournalHeader from '../journalHeader/JournalHeader';
import { message, Spin, Alert, Button } from 'antd';
import FilterJournalStock from '../filterJournalStock/FilterJournalStock';
import RapportKPIsStock from './rapportKPIsStock/RapportKPIsStock';
import PointurePlus from './pointurePlus/PointurePlus';
import TraitementToday from './traitementToday/TraitementToday';
import TopModele from './topModele/TopModele';
import StockAnciennete from './stockAnciennete/StockAnciennete';
import { getDashboardStock } from '../../../services/rapportService';
import useDashboardData from './hooks/useDashboardData';

const DashbordStock = () => {
    // ✅ Utiliser le hook pour les données
    const { 
        data: hookData, 
        loading: hookLoading, 
        filters: hookFilters,
        fetchData: hookFetchData,
        refresh: hookRefresh
    } = useDashboardData();

    // ✅ État local pour les données transformées
    const [dashboardData, setDashboardData] = useState({
        kpis: {
            total_stock: 0,
            valeur_stock: 0,
            sortieJour: 0,
            alerts: 0
        },
        stockAnciennete: [],
        topModeles: [],
        pointures: [],
        traitements: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentFilters, setCurrentFilters] = useState({
        id_marque: null,
        id_taille: null,
        date_debut: null,
        date_fin: null
    });

    // ✅ Mettre à jour dashboardData quand hookData change
    useEffect(() => {
        if (hookData) {
            // ✅ Mapper les données du backend vers le format attendu
            const mappedKpis = {
                total_stock: hookData.kpis?.stock_actuel || 0,
                valeur_stock: hookData.kpis?.ca_brut || 0,
                sortieJour: hookData.kpis?.ventes_nettes || 0,
                alerts: (hookData.kpis?.ruptures || 0) + (hookData.kpis?.stock_faible || 0)
            };

            setDashboardData({
                kpis: mappedKpis,
                stockAnciennete: hookData.stockAnciennete || [],
                topModeles: hookData.topModeles || [],
                pointures: hookData.pointures || [],
                traitements: hookData.traitements || { ruptures: [], dormants: [], recommander: [] }
            });
        }
    }, [hookData]);

    // ✅ Mettre à jour loading
    useEffect(() => {
        setLoading(hookLoading);
    }, [hookLoading]);

    // ✅ Fonction pour charger les données avec filtres
    const fetchData = useCallback(async (filters = {}) => {
        setError(null);
        
        try {
            // Filtrer les valeurs null/undefined
            const cleanFilters = {};
            
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    if (Array.isArray(value) && value.length === 0) {
                        return;
                    }
                    cleanFilters[key] = value;
                }
            });
            
            setCurrentFilters(cleanFilters);
            
            // ✅ Utiliser le hook pour fetch
            await hookFetchData(cleanFilters);
            
        } catch (error) {
            console.error('Erreur lors du chargement des données:', error);
            setError(error.message || 'Impossible de charger les données du dashboard');
            message.error('Erreur lors du chargement des données');
        }
    }, [hookFetchData]);

    // ✅ Gestionnaire de filtre
    const handleFilter = useCallback((filters) => {
        // Validation des dates
        if (filters.date_debut && filters.date_fin) {
            const debut = new Date(filters.date_debut);
            const fin = new Date(filters.date_fin);
            
            if (debut > fin) {
                message.error("La date de début doit être antérieure à la date de fin");
                return;
            }
            
            const diffDays = Math.ceil((fin - debut) / (1000 * 60 * 60 * 24));
            if (diffDays > 365) {
                message.warning("La période sélectionnée est très large, cela peut affecter les performances");
            }
        }

        fetchData(filters);
    }, [fetchData]);

    // ✅ Chargement initial
    useEffect(() => {
        fetchData();
    }, []);

    // ✅ Fonction pour rafraîchir les données
    const handleRefresh = useCallback(() => {
        fetchData(currentFilters);
    }, [fetchData, currentFilters]);

    // États de chargement et d'erreur
    if (loading && !dashboardData.kpis.total_stock) {
        return (
            <div className="dashbordStock">
                <JournalHeader title="Tableau de bord Stock" />
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" tip="Chargement du tableau de bord..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashbordStock">
                <JournalHeader title="Tableau de bord Stock" />
                <Alert
                    message="Erreur de chargement"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" type="primary" onClick={handleRefresh}>
                            Réessayer
                        </Button>
                    }
                    style={{ margin: '20px' }}
                />
            </div>
        );
    }

    return (
        <div className="dashbordStock">
            <JournalHeader title="Tableau de bord Stock" />
            
            <FilterJournalStock 
                onSearch={handleFilter}
                initialFilters={currentFilters}
                loading={loading}
            />
            
            {/* Indicateur de chargement pour les mises à jour */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                    <Spin tip="Mise à jour..." />
                </div>
            )}
            
            <RapportKPIsStock kpis={dashboardData.kpis} />
            
            <div className="dashbordStock_wrapper">
                <PointurePlus pointures={dashboardData.pointures} />
                <TraitementToday traitements={dashboardData.traitements} />
            </div>

            <div className="dashbordStock_wrapper">
                <TopModele tops={dashboardData.topModeles} />
                <StockAnciennete stock={dashboardData.stockAnciennete} />
            </div>
        </div>
    );
};

export default DashbordStock;